// @flow

import prop from 'lodash/fp/prop';
import join from 'lodash/fp/join';
import find from 'lodash/fp/find';
import slice from 'lodash/fp/slice';
import values from 'lodash/fp/values';
import flow from 'lodash/fp/flow';
import propEq from 'lodash/fp/propEq';
import head from 'lodash/fp/head';
import Alert from 'react-aaui/es/alert';
import { activate } from 'modules/rootEpic';
import { ActionsObservable } from 'redux-observable';
import { get, getEventResult, getLeaderBoardResult } from 'modules/event/rest';
import {
  list as getParticipants,
  getByNumber as getParticipantByNumber,
} from 'modules/participant/rest';
import { getRoutes, getHistory } from 'modules/route/rest';
import 'rxjs/add/operator/mergeMap';
import { forkJoin } from 'rxjs/observable/forkJoin';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/filter';
import ajax from 'services/ajax/ajax';
import Maybe, { maybe } from '@active/awe-ui-core/lib/fp/Maybe';
import { normalize } from 'normalizr';
import ParticipantEntity from 'modules/participant/entity';

import { buildQueryExpression } from 'services/query/query';
import { createFromVirtualEvent } from 'modules/result/rest';
import { appError } from 'modules/app/duck';

import {
  MOUNT,
  mounted,
  MOUNTED,
  fetchEventDone,
  SEARCH_PARTICIPANT,
  searchParticipantDone,
  FETCH_EVENT_RESULT,
  fetchEventResult,
  fetchEventResultDone,
  FETCH_LEADER_BOARD_RESULT,
  fetchLeaderBoardResultDone,
  CHANGE_RESULT_TYPE,
  changeResultType,
  FETCH_IMPORT_HISTORY,
  fetchImportHistoryDone,
  FETCH_IMPORT_ROUTES,
  fetchImportRoutesDone,
  UPLOAD_RESULT_FILE,
  uploadResultFileDone,
  IMPORT_RESULT,
  importResultDone,
  IMPORT_RESULT_DONE,
  fetchImportHistory,
  fetchImportRoutes,
  clearUploadFile,
  CHANGE_COURSE_ID,
  CHANGE_DIVISION_ID,
  CHANGE_VIEW_LIMIT,
  CHANGE_LEADER_BOARD_COURSE_ID,
  CHANGE_LEADER_BOARD_DIVISION_ID,
  fetchLeaderBoardResult,
  clearLeaderBoardResult,
  searchParticipantByNumberDone,
  searchParticipantByNumberFailed,
  SEARCH_PARTICIPANT_BY_NUMBER,
  SUBMIT_VIRTUAL_EVENT_RESULT,
  submitVirtualEventResultDone,
  submitVirtualEventResultFailed,
  CHANGE_EVENT_VIEW_TYPE,
} from './duck';

import {
  getResultRouteId,
  getResultDivisionId,
  getApplicationName,
  getEventLimit,
  sortRouteGroup,
  getResultType,
  getEventViewType,
} from './selector';

const RESULTS_NOT_FOUND = 'RESULTS_NOT_FOUND';

export const mountEpic = (actions$: ActionsObservable) => actions$.ofType(MOUNT).mergeMap(
  action => get(action.payload.applicationName, (resp) => {
    const routes = resp.routes || [];
    const groupSortedRoutes = routes.map((route) => {
      return {
        ...route,
        groups: route.groups ? sortRouteGroup(route.groups) : [],
      };
    });
    const sortedRouteGroupevent = {
      ...resp,
      routes: groupSortedRoutes,
    };
    return [
      mounted(sortedRouteGroupevent),
      fetchEventDone(sortedRouteGroupevent),
    ];
  }),
);

export const searchParticipantEpic = (actions$: ActionsObservable) => actions$.ofType(SEARCH_PARTICIPANT)
  .mergeMap((action) => {
    const keyword = action.payload.value;
    const q = buildQueryExpression(keyword);
    return getParticipants({
      applicationName: action.payload.applicationName,
      q,
      offset: action.payload.offset,
      limit: 10,
    }, (response) => {
      const normalizedParticipants = normalize(response.items, [ ParticipantEntity ]);
      const total = response.meta.totalCount;
      const recordsLength = normalizedParticipants.result.length;
      return searchParticipantDone({
        entities: normalizedParticipants.entities,
        result: normalizedParticipants.result,
        total,
        recordsLength,
      });
    });
  });

export const initialFetchEventResultEpic = (action$: ActionsObservable, store) => {
  return action$
    .ofType(MOUNTED)
    .mergeMap(({ payload }) => {
      let courseId;
      const state = store.getState();
      const event = payload.entities.events[payload.result];
      const routes = values(payload.entities.routes);
      const { applicationName } = event;
      const viewType = getEventViewType(state);

      if (event.routes) {
        courseId = viewType ? flow(
          find(propEq('virtual', viewType === 'VIRTUAL')),
          prop('id'),
        )(routes) : flow(head, prop('id'))(routes);
      }

      const divisionId = maybe(
        '',
        gs => find(group => prop('groupType')(prop(group)(payload.entities.groups)) === 'DIVISION')(gs),
        Maybe.of(payload.entities.routes)
          .map(prop(courseId))
          .map(prop('groups')));
      if (courseId && divisionId) {
        return forkJoin(
          getEventResult({
            courseId, divisionId, applicationName, offset: 0, limit: 10,
          }, v => ({ eventResult: v })),
          getLeaderBoardResult({ courseId, divisionId, applicationName }, v => ({ leaderBoardResult: v })),
        ).map((vals) => {
          if (vals[0].eventResult.length === 0 && vals[1].leaderBoardResult.length > 0) {
            return changeResultType('LEADER_BOARD');
          }
          return fetchEventResultDone(vals[0].eventResult);
        });
      }
      return ActionsObservable.of(fetchEventResultDone([]));
    });
};

export const fetchEventResultEpic = (action$: ActionsObservable) => {
  return action$
    .ofType(FETCH_EVENT_RESULT)
    .mergeMap(({
      payload: {
        courseId, divisionId, applicationName, offset, limit,
      },
    }) => getEventResult({
      courseId,
      divisionId,
      applicationName,
      offset,
      limit,
    }, result => fetchEventResultDone(result), (error) => {
      if (prop('status')(error) === 404) {
        return [
          fetchEventResultDone({ meta: [], items: [] }),
          appError(404, RESULTS_NOT_FOUND),
        ];
      }
      return fetchEventResultDone({ meta: [], items: [] });
    }),
    );
};

export const fetchLeaderBoardResultEpic = (action$: ActionsObservable) => {
  return action$
    .ofType(FETCH_LEADER_BOARD_RESULT)
    .mergeMap(({ payload: { courseId, divisionId, applicationName } }) => getLeaderBoardResult({
      courseId,
      divisionId,
      applicationName,
    }, fetchLeaderBoardResultDone, (error) => {
      if (prop('status')(error) === 404) {
        return [
          fetchLeaderBoardResultDone([]),
          appError(404, RESULTS_NOT_FOUND),
        ];
      }
      return fetchLeaderBoardResultDone([]);
    }));
};

export const fetchImportRoutesEpic = (action$: ActionsObservable) => {
  return action$
    .ofType(FETCH_IMPORT_ROUTES)
    .mergeMap(({ payload: { applicationName } }) => getRoutes({
      applicationName,
    }, fetchImportRoutesDone));
};

export const fetchImportHistoryEpic = (action$: ActionsObservable) => {
  return action$
    .ofType(FETCH_IMPORT_HISTORY)
    .mergeMap(({ payload: { applicationName } }) => getHistory({
      applicationName,
    }, fetchImportHistoryDone));
};

export const uploadResultFileEpic = (action$: ActionsObservable) => {
  return action$
    .ofType(UPLOAD_RESULT_FILE)
    .mergeMap(({ payload }) => {
      const formData = new FormData();
      formData.append('file', payload.file);

      const config = {
        url: '/file/upload',
        body: formData,
        autoContentType: true,
        headers: {
          accept: 'application/vnd.active.results-api.v1+json',
        },
      };

      return ajax.post(config, {
        mapSuccess: ({ uploadId }) => uploadResultFileDone(uploadId),
        mapFailure: clearUploadFile,
      });
    });
};

export const importResultEpic = (action$: ActionsObservable) => {
  return action$
    .ofType(IMPORT_RESULT)
    .mergeMap(({ payload: { applicationName, config } }) => ajax.post({
      url: `/results/events/${applicationName}/result/import`,
      body: JSON.stringify(config),
      headers: {
        'Content-Type': 'application/vnd.active.results-service.v1+json',
      },
    }, {
      mapSuccess: (result) => {
        if (result.errors && result.errors.length > 0) {
          const errorStr = join('\n')(slice(0, 5)(result.errors).map(
            error => `${error.identifier} ${error.msg} line:${error.lineNumber || ''}`,
          ));
          Alert.error(errorStr);
          return clearUploadFile();
        }
        Alert.info('completed');

        return importResultDone(applicationName);
      },
      mapFailure: clearUploadFile,
    },
    ),
    );
};

export const importResultFetchHistoryEpic = (action$: ActionsObservable) => {
  return action$
    .ofType(IMPORT_RESULT_DONE)
    .map(({ payload: { applicationName } }) => {
      return fetchImportHistory(applicationName);
    });
};

export const importResultFetchRoutesEpic = (action$: ActionsObservable) => {
  return action$
    .ofType(IMPORT_RESULT_DONE)
    .map(({ payload: { applicationName } }) => {
      return fetchImportRoutes(applicationName);
    });
};

export const changeResultTypeEpic = (action$: ActionsObservable, store) => {
  return action$
    .ofType(CHANGE_RESULT_TYPE)
    .filter(({ payload: { resultType } }) => resultType === 'RESULT' && getResultRouteId(store.getState()) && getResultDivisionId(store.getState()))
    .map(() => {
      const state = store.getState();
      return fetchEventResult({
        applicationName: getApplicationName(state),
        courseId: getResultRouteId(state),
        divisionId: getResultDivisionId(state),
        offset: 0,
        limit: getEventLimit(state),
      });
    });
};

export const changeEventViewTypeEpic = (action$: ActionsObservable, store) => {
  return action$
    .ofType(CHANGE_EVENT_VIEW_TYPE)
    .filter(({ payload: { init } }) => !init)
    .map(() => {
      const state = store.getState();
      const resultType = getResultType(state);

      return changeResultType(resultType);
    });
};

export const changeResultCourseIdEpic = (action$: ActionsObservable, store) => {
  return action$
    .ofType(CHANGE_COURSE_ID)
    .filter(({ payload: { divisionId } }) => !!divisionId)
    .map(({ payload: { courseId, divisionId } }) => {
      const state = store.getState();
      return fetchEventResult({
        applicationName: getApplicationName(state),
        courseId,
        divisionId,
        offset: 0,
        limit: getEventLimit(state),
      });
    });
};

export const changeResultDivisionIdEpic = (action$: ActionsObservable, store) => {
  return action$
    .ofType(CHANGE_DIVISION_ID)
    .map(({ payload: { id } }) => {
      const state = store.getState();
      return fetchEventResult({
        applicationName: getApplicationName(state),
        courseId: getResultRouteId(state),
        divisionId: id,
        offset: 0,
        limit: getEventLimit(state),
      });
    });
};

export const changeResultViewLimitEpic = (action$: ActionsObservable, store) => {
  return action$
    .ofType(CHANGE_VIEW_LIMIT)
    .map(({ payload: { id } }) => {
      const state = store.getState();
      return fetchEventResult({
        applicationName: getApplicationName(state),
        courseId: getResultRouteId(state),
        divisionId: getResultDivisionId(state),
        offset: 0,
        limit: id,
      });
    });
};

export const changeLeaderBoardCourseIdEpic = (action$: ActionsObservable, store) => {
  return action$
    .ofType(CHANGE_LEADER_BOARD_COURSE_ID)
    .map(({ payload: { courseId, divisionId } }) => {
      const state = store.getState();
      if (divisionId) {
        return fetchLeaderBoardResult({
          applicationName: getApplicationName(state),
          courseId,
          divisionId,
        });
      }
      return clearLeaderBoardResult();
    });
};

export const changeLeaderBoardDivisionIdEpic = (action$: ActionsObservable, store) => {
  return action$
    .ofType(CHANGE_LEADER_BOARD_DIVISION_ID)
    .map(({ payload: { id } }) => {
      const state = store.getState();
      return fetchLeaderBoardResult({
        applicationName: getApplicationName(state),
        courseId: getResultRouteId(state),
        divisionId: id,
      });
    });
};

export const searchParticipantByNumberEpic = (
  actions$: ActionsObservable,
) => actions$.ofType(SEARCH_PARTICIPANT_BY_NUMBER).mergeMap((
  { payload },
) => getParticipantByNumber(
  payload,
  searchParticipantByNumberDone,
  searchParticipantByNumberFailed,
));

export const submitVirtualEventResultEpic = (
  actions$: ActionsObservable,
) => actions$.ofType(SUBMIT_VIRTUAL_EVENT_RESULT).mergeMap((
  { payload },
) => createFromVirtualEvent(
  payload,
  submitVirtualEventResultDone,
  submitVirtualEventResultFailed,
));

activate(mountEpic);
activate(searchParticipantEpic);
activate(initialFetchEventResultEpic);
activate(fetchEventResultEpic);
activate(fetchLeaderBoardResultEpic);
activate(fetchImportRoutesEpic);
activate(fetchImportHistoryEpic);
activate(uploadResultFileEpic);
activate(importResultEpic);
activate(importResultFetchHistoryEpic);
activate(importResultFetchRoutesEpic);
activate(changeResultTypeEpic);
activate(changeResultCourseIdEpic);
activate(changeResultDivisionIdEpic);
activate(changeResultViewLimitEpic);
activate(changeLeaderBoardCourseIdEpic);
activate(changeLeaderBoardDivisionIdEpic);
activate(searchParticipantByNumberEpic);
activate(submitVirtualEventResultEpic);
activate(changeEventViewTypeEpic);
